import { Box, Heading, Switch, Text, TextInput } from '@razorpay/blade/components';
import { Reveal } from 'components/ui/Reveal/Reveal';
import React from 'react';
import { AdvancedFBPSettingsWindowConfigurationProps } from '../types';
import CustomWindowSettings from './CustomWindowSettings';
import { FBPSettingsDescriptions } from '../constants';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';

const AdvancedWindowConfiguration = ({
  isLoading,
  settings,
  onChange,
  orgId,
}: AdvancedFBPSettingsWindowConfigurationProps) => {
  const { isM3S2Enabled } = useIsFeatureDisabledForRearch();

  if (!orgId) {
    return null;
  }

  return (
    <Box marginX={{ base: 'spacing.8', l: 'auto' }} width="500px" paddingY="spacing.11">
      <Heading size="small">Advance window configuration</Heading>
      <Box marginTop="spacing.10" display="flex" flexDirection="column" gap="spacing.7">
        <Box
          borderWidth="thin"
          borderColor="surface.border.gray.muted"
          borderRadius="medium"
          padding="spacing.7"
          backgroundColor="surface.background.gray.moderate">
          <Box display="flex" gap="spacing.8" justifyContent="space-between">
            <Box>
              <Text weight="semibold" color="surface.text.gray.subtle">
                Auto open declaration window for new employees
              </Text>
              <Text
                variant="caption"
                marginTop="spacing.2"
                color="surface.text.gray.muted"
                size="small">
                {
                  FBPSettingsDescriptions['auto-open-declaration-window-new-employees'][
                    isM3S2Enabled ? 'M3S2' : 'DEFAULT'
                  ]
                }
              </Text>
            </Box>
            <Switch
              isDisabled={isLoading}
              isChecked={settings.isAutoOpenWindowForNewEmployeesEnabled}
              size="small"
              accessibilityLabel="FBP Allowance Toggle"
              alignSelf="flex-start"
              onChange={({ isChecked }) =>
                onChange('isAutoOpenWindowForNewEmployeesEnabled', isChecked)
              }
            />
          </Box>
          <Reveal isOpen={settings.isAutoOpenWindowForNewEmployeesEnabled}>
            <Box paddingTop="spacing.7">
              <Box
                paddingTop="spacing.7"
                borderTopWidth="thin"
                borderTopColor="surface.border.gray.subtle">
                <Box display="flex" gap="spacing.3" alignItems="center">
                  <Text weight="semibold" color="surface.text.gray.subtle">
                    How many days should the declaration window remain open after the employee joins
                  </Text>
                </Box>

                <Box paddingTop={'spacing.4'}>
                  <Box width="50%">
                    <TextInput
                      value={settings.autoOpenDeclarationWindowForNewEmployees || ''}
                      accessibilityLabel=""
                      onChange={({ value }) =>
                        onChange('autoOpenDeclarationWindowForNewEmployees', value || '')
                      }
                    />
                  </Box>
                  {settings.autoOpenDeclarationWindowForNewEmployees ? (
                    <Text
                      marginTop="spacing.4"
                      variant="caption"
                      color="interactive.text.information.subtle"
                      size="small">
                      {`⚡️ Declaration window will be open for ${settings.autoOpenDeclarationWindowForNewEmployees} days after the employee's joining date`}
                    </Text>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Reveal>
        </Box>

        {/* Salary Revision */}
        <Box
          borderWidth="thin"
          borderColor="surface.border.gray.muted"
          borderRadius="medium"
          padding="spacing.7"
          backgroundColor="surface.background.gray.moderate">
          <Box display="flex" gap="spacing.8" justifyContent="space-between">
            <Box>
              <Text weight="semibold" color="surface.text.gray.subtle">
                Auto-open declaration window for salary-revised employees
              </Text>
              <Text
                variant="caption"
                marginTop="spacing.2"
                color="surface.text.gray.muted"
                size="small">
                {
                  FBPSettingsDescriptions['auto-open-declaration-window-salary-revised-employees'][
                    isM3S2Enabled ? 'M3S2' : 'DEFAULT'
                  ]
                }
              </Text>
            </Box>
            <Switch
              isDisabled={isLoading}
              isChecked={settings.isAutoOpenWindowForSalaryRevisedEmployeesEnabled}
              size="small"
              accessibilityLabel="FBP Allowance Toggle"
              alignSelf="flex-start"
              onChange={({ isChecked }) =>
                onChange('isAutoOpenWindowForSalaryRevisedEmployeesEnabled', isChecked)
              }
            />
          </Box>
          <Reveal isOpen={settings.isAutoOpenWindowForSalaryRevisedEmployeesEnabled}>
            <Box paddingTop="spacing.7">
              <Box
                paddingTop="spacing.7"
                borderTopWidth="thin"
                borderTopColor="surface.border.gray.subtle">
                <Box display="flex" gap="spacing.3" alignItems="center">
                  <Text weight="semibold" color="surface.text.gray.subtle">
                    Specify the declaration window duration for salary-revised employees
                  </Text>
                </Box>

                <Box paddingTop={'spacing.4'}>
                  <Box width="50%">
                    <TextInput
                      accessibilityLabel=""
                      value={settings.autoOpenDeclarationWindowForSalaryRevisedEmployees || ''}
                      onChange={({ value }) =>
                        onChange('autoOpenDeclarationWindowForSalaryRevisedEmployees', value || '')
                      }
                    />
                  </Box>
                  {settings.autoOpenDeclarationWindowForSalaryRevisedEmployees ? (
                    <Text
                      marginTop="spacing.4"
                      variant="caption"
                      color="interactive.text.information.subtle"
                      size="small">
                      {`⚡️ Declaration window will be open for ${settings.autoOpenDeclarationWindowForSalaryRevisedEmployees} days after salary is revised for an employee. `}
                    </Text>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Reveal>
        </Box>

        {/* Dismissal */}
        <Box
          borderWidth="thin"
          borderColor="surface.border.gray.muted"
          borderRadius="medium"
          padding="spacing.7"
          backgroundColor="surface.background.gray.moderate">
          <Box display="flex" gap="spacing.8" justifyContent="space-between">
            <Box>
              <Text weight="semibold" color="surface.text.gray.subtle">
                Allow employees to upload FBP proofs until dismissal
              </Text>
              <Text
                variant="caption"
                marginTop="spacing.2"
                color="surface.text.gray.muted"
                size="small">
                {
                  FBPSettingsDescriptions['proof-upload-till-dismissal-config'][
                    isM3S2Enabled ? 'M3S2' : 'DEFAULT'
                  ]
                }
              </Text>
            </Box>
            <Switch
              isDisabled={isLoading}
              isChecked={settings.allowProofsUploadTillDismissal}
              size="small"
              accessibilityLabel="FBP Allowance Toggle"
              alignSelf="flex-start"
              onChange={({ isChecked }) => onChange('allowProofsUploadTillDismissal', isChecked)}
            />
          </Box>
        </Box>
      </Box>
      <CustomWindowSettings orgId={orgId} />
    </Box>
  );
};

export default AdvancedWindowConfiguration;
